import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ImagePlus, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { calcRFromPnl, fmtR } from '../../lib/tradeUtils';

const empty = { ticker: '', pnl: '', risk_amount: '', r_multiple: '', traded_at: '', notes: '', screenshot_key: null };

function toLocalInput(iso) {
  const d = iso ? new Date(iso) : new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function LogTradeModal({ open, onClose, onSaved, trade }) {
  const isEdit = !!trade;
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [manualR, setManualR] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (trade) {
      setF({
        ticker: trade.ticker || '', pnl: trade.pnl ?? '', risk_amount: trade.risk_amount ?? '',
        r_multiple: trade.r_multiple ?? '', traded_at: toLocalInput(trade.traded_at),
        notes: trade.notes || '', screenshot_key: trade.screenshot_key || null,
      });
      setManualR(trade.pnl === null && trade.r_multiple !== null);
    } else {
      setF({ ...empty, traded_at: toLocalInput(null) });
      setManualR(false);
    }
  }, [open, trade]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const autoR = calcRFromPnl(f.pnl, f.risk_amount);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await api.post('/trades/screenshot-presign', {
        filename: file.name, content_type: file.type || 'image/png', size: file.size,
      });
      await fetch(data.put_url, { method: 'PUT', body: file, headers: { 'Content-Type': data.content_type || 'image/png' } });
      set('screenshot_key', data.key);
      toast.success('Screenshot attached');
    } catch (err) {
      if (err?.response?.status === 501) toast.error('Screenshot upload is not available right now.');
      else toast.error('Could not upload screenshot');
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.ticker.trim()) return toast.error('Ticker is required');
    setBusy(true);
    const num = (v) => (v === '' || v === null ? null : parseFloat(v));
    const payload = {
      ticker: f.ticker.trim().toUpperCase(),
      pnl: num(f.pnl),
      risk_amount: num(f.risk_amount),
      r_multiple: manualR ? num(f.r_multiple) : null,
      traded_at: f.traded_at ? new Date(f.traded_at).toISOString() : null,
      notes: f.notes,
      screenshot_key: f.screenshot_key,
    };
    try {
      if (isEdit) await api.patch(`/trades/${trade.trade_id}`, payload);
      else await api.post('/trades', payload);
      toast.success(isEdit ? 'Trade updated' : 'Trade logged');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save trade');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-4 sm:p-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97, y: 12 }}
            transition={{ type: 'spring', duration: 0.3 }}
            className="relative z-10 my-4 w-full max-w-md rounded-2xl border border-white/10 bg-ink-card p-6"
            data-testid="log-trade-modal"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-white">{isEdit ? 'Edit Trade' : 'Log Trade'}</h2>
              <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="log-trade-close"><X size={20} /></button>
            </div>

            <form onSubmit={submit} className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block text-xs text-zinc-400">Ticker</label>
                <Input value={f.ticker} onChange={(e) => set('ticker', e.target.value.toUpperCase())} placeholder="TSLA" data-testid="trade-ticker" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs text-zinc-400">Total P&L ($)</label>
                  <Input type="number" step="any" value={f.pnl} onChange={(e) => set('pnl', e.target.value)} placeholder="e.g. 250 or -100" data-testid="trade-pnl" />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-zinc-400">Risk ($)</label>
                  <Input type="number" step="any" value={f.risk_amount} onChange={(e) => set('risk_amount', e.target.value)} placeholder="e.g. 100" data-testid="trade-risk" />
                </div>
              </div>

              {/* R preview / manual */}
              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-ink-surface px-4 py-3">
                <div>
                  <div className="text-xs text-zinc-500">R Multiple {manualR ? '(manual)' : '= P&L ÷ Risk'}</div>
                  {manualR ? (
                    <Input type="number" step="any" value={f.r_multiple} onChange={(e) => set('r_multiple', e.target.value)} placeholder="e.g. 2.5" className="mt-1 h-9 w-28" data-testid="trade-manual-r" />
                  ) : (
                    <div className="mt-0.5 font-heading text-2xl font-bold tnum text-emerald" data-testid="trade-auto-r">
                      {autoR === null ? '—' : fmtR(autoR, 2)}
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => setManualR((v) => !v)} className="text-xs font-semibold text-zinc-400 underline-offset-2 hover:text-white hover:underline" data-testid="trade-toggle-manual-r">
                  {manualR ? 'Use P&L ÷ Risk' : 'Enter R manually'}
                </button>
              </div>

              <div>
                <label className="mb-1 block text-xs text-zinc-400">Date / Time</label>
                <Input type="datetime-local" value={f.traded_at} onChange={(e) => set('traded_at', e.target.value)} data-testid="trade-date" />
              </div>

              <div>
                <label className="mb-1 block text-xs text-zinc-400">Notes</label>
                <Textarea rows={3} value={f.notes} onChange={(e) => set('notes', e.target.value)} placeholder="What was the thesis? How did you manage it?" data-testid="trade-notes" />
              </div>

              <div>
                <label className="mb-1 block text-xs text-zinc-400">Screenshot (optional)</label>
                <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-zinc-700 px-4 py-3 text-sm text-zinc-400 hover:border-zinc-500" data-testid="trade-screenshot-label">
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : f.screenshot_key ? <Check size={16} className="text-emerald" /> : <ImagePlus size={16} />}
                  {uploading ? 'Uploading…' : f.screenshot_key ? 'Screenshot attached — replace' : 'Attach chart screenshot'}
                  <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="trade-screenshot-input" />
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-1">
                <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
                <Button type="submit" variant="emerald" disabled={busy || uploading} data-testid="trade-submit">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                  {isEdit ? 'Save Changes' : 'Log Trade'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
